import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import { getForm1099BoxLabel } from "@common/constants/domains/accounting/form1099Box";
import { displayAccountLabel } from "@common/helpers/accounting";
import { fullDateLongFormat } from "@common/helpers/date";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { formatDateOnly } from "@helpers/date";
import { useIdCopy } from "@hooks/useIdCopy";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { TransactionClassCell } from "../generalLedger/Cells/GLClassCell";
import { GL_COPY_LABEL } from "../generalLedger/Cells/glCellHelpers";
import {
    JournalLineBalanceCell,
    JournalLineReferenceCell,
    JournalLineSourceCell,
} from "../generalLedger/Cells/JournalLineCells";
import generalLedgerStyles from "../generalLedger/GeneralLedgerTable.module.scss";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { Form1099NecRow, Form1099TableCallbacks } from "./form1099NecFormat";

type Form1099CellContext = CellContext<
    ViewTableFeatures,
    Form1099NecRow,
    unknown
>;

export const Form1099NecDateCell = React.memo(function Form1099NecDateCell({
    row,
}: Form1099CellContext): React.ReactElement {
    const { rowKind, vendorName, transaction } = row.original;

    if (rowKind === "vendor") {
        return (
            <ViewTableCell className={generalLedgerStyles.accountCell}>
                {vendorName}
            </ViewTableCell>
        );
    }
    if (rowKind === "closingBalance") {
        return (
            <ViewTableCell
                className={generalLedgerStyles.endingBalanceLabelCell}
            >
                Ending balance
            </ViewTableCell>
        );
    }
    if (rowKind === "transaction" && transaction) {
        return (
            <ViewTableCell className={generalLedgerStyles.transactionCell}>
                {formatDateOnly(transaction.dateOnly, fullDateLongFormat)}
            </ViewTableCell>
        );
    }
    return <ViewTableCell />;
});

export const Form1099NecSourceCell = React.memo(function Form1099NecSourceCell({
    row,
    table,
}: Form1099CellContext): React.ReactElement {
    const { rowKind, transaction } = row.original;
    const meta = useViewTableMeta<Form1099TableCallbacks, unknown>(table);

    if (rowKind !== "transaction" || !transaction) {
        return <ViewTableCell />;
    }

    const onSourceClick = meta?.callbacks?.onSourceClick;

    return (
        <JournalLineSourceCell
            source={transaction.source}
            isRecurring={!!transaction.recurringRuleId}
            onClick={
                onSourceClick ? () => onSourceClick(transaction) : undefined
            }
        />
    );
});

export const Form1099NecReferenceCell = React.memo(
    function Form1099NecReferenceCell({
        row,
        table,
    }: Form1099CellContext): React.ReactElement {
        const { rowKind, transaction } = row.original;
        const meta = useViewTableMeta<Form1099TableCallbacks, unknown>(table);

        if (rowKind !== "transaction" || !transaction) {
            return <ViewTableCell />;
        }

        const onReferenceClick = meta?.callbacks?.onReferenceClick;

        return (
            <JournalLineReferenceCell
                referenceNumber={transaction.referenceNumber}
                onClick={
                    onReferenceClick
                        ? () => onReferenceClick(transaction)
                        : undefined
                }
            />
        );
    },
);

export const Form1099NecDescriptionCell = React.memo(
    function Form1099NecDescriptionCell({
        row,
    }: Form1099CellContext): React.ReactElement {
        const { rowKind, transaction } = row.original;
        const { getCopyProps } = useIdCopy({ label: GL_COPY_LABEL });

        if (rowKind !== "transaction" || !transaction) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell {...getCopyProps(transaction.referenceNumber)}>
                {transaction.bankDescription ?? transaction.description}
            </ViewTableCell>
        );
    },
);

export const Form1099NecBoxCell = React.memo(function Form1099NecBoxCell({
    row,
}: Form1099CellContext): React.ReactElement {
    const { rowKind, transaction } = row.original;
    const { getCopyProps } = useIdCopy({ label: GL_COPY_LABEL });

    if (rowKind !== "transaction" || !transaction) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell {...getCopyProps(transaction.referenceNumber)}>
            {getForm1099BoxLabel(transaction.form1099Box)}
        </ViewTableCell>
    );
});

export const Form1099NecAccountCell = React.memo(
    function Form1099NecAccountCell({
        row,
    }: Form1099CellContext): React.ReactElement {
        const { rowKind, transaction } = row.original;
        const showAccountNumbers = useShowAccountNumbers();
        const { getCopyProps } = useIdCopy({ label: GL_COPY_LABEL });

        if (rowKind !== "transaction" || !transaction) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell {...getCopyProps(transaction.referenceNumber)}>
                {displayAccountLabel(
                    {
                        name: transaction.accountName,
                        codeText: transaction.accountCodeText,
                    },
                    showAccountNumbers,
                )}
            </ViewTableCell>
        );
    },
);

export const Form1099NecSplitCell = React.memo(function Form1099NecSplitCell({
    row,
}: Form1099CellContext): React.ReactElement {
    const { rowKind, transaction } = row.original;

    if (rowKind !== "transaction" || !transaction) {
        return <ViewTableCell />;
    }

    return <ViewTableCell>{transaction.splitLabel}</ViewTableCell>;
});

export const Form1099NecClassCell = React.memo(function Form1099NecClassCell(
    props: Form1099CellContext,
): React.ReactElement {
    return <TransactionClassCell {...props} />;
});

export const Form1099NecAmountCell = React.memo(function Form1099NecAmountCell({
    row,
}: Form1099CellContext): React.ReactElement {
    const { rowKind, transaction } = row.original;
    const { text, negativeClassName } = useReportAmountDisplay(
        rowKind === "transaction" && transaction ? transaction.amount : 0,
    );

    if (rowKind !== "transaction" || !transaction) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell
            className={classNames(
                generalLedgerStyles.numericCell,
                negativeClassName,
            )}
        >
            {text}
        </ViewTableCell>
    );
});

export const Form1099NecBalanceCell = ({
    row,
}: Readonly<Form1099CellContext>): React.ReactElement => {
    const { rowKind, closingBalance, balanceValue, transaction } = row.original;

    if (rowKind === "vendor") {
        return (
            <RowExpansion row={row}>
                {(isExpanded) =>
                    isExpanded ? (
                        <ViewTableCell />
                    ) : (
                        <JournalLineBalanceCell
                            kind="group"
                            amount={closingBalance ?? 0}
                        />
                    )
                }
            </RowExpansion>
        );
    }
    if (rowKind === "closingBalance") {
        return (
            <JournalLineBalanceCell
                kind="closingBalance"
                amount={balanceValue ?? 0}
            />
        );
    }
    if (rowKind === "transaction" && transaction) {
        return (
            <JournalLineBalanceCell
                kind="transaction"
                amount={transaction.balance}
            />
        );
    }
    return <ViewTableCell />;
};
